import { effect, inject, Injectable, Injector } from '@angular/core';
import { PushNotificationService } from './push-notification.service';
import { AuthService } from '@auth0/auth0-angular';
import { toSignal } from '@angular/core/rxjs-interop';
import { Overlay, type OverlayRef } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { assertIsNotNil } from '@pct-ts/assert';
import { ComponentPortal } from '@angular/cdk/portal';
import { SubscribePushNotificationOverlayPanelComponent } from './subscribe-push-notification-overlay-panel.component';
import {
  CLOSE_HANDLER_TOKEN,
  SUBSCRIBE_HANDLER_TOKEN,
  type SubscribeHandler,
} from './subscribe-handle.token';
import {
  openErrorToast,
  openSuccessToast,
  openWarningToast,
} from '@pct-ng/feature-message-material';
import { DeviceTokenService } from '@szebit-app/sdk/backend';
import { finalize } from 'rxjs';
import { UAParser } from 'ua-parser-js';

const LOCALSTORAGE_KEY = 'HasUserBeenShownSubscriptionPushNotificationPopup';

@Injectable({ providedIn: 'root' })
export class AutoSubscribePushNotificationService {
  readonly #injector = inject(Injector);
  readonly #deviceTokenService = Injector.create({
    parent: this.#injector,
    providers: [DeviceTokenService],
  }).get(DeviceTokenService);
  readonly #pushNotificationService = inject(PushNotificationService, { optional: true });
  readonly #overlay = inject(Overlay);
  readonly #window = inject(DOCUMENT).defaultView;
  readonly #isAuthenticated = toSignal(inject(AuthService).isAuthenticated$);

  #timerId: number | null = null;
  #overlayRef: OverlayRef | null = null;

  constructor() {
    if (this.#pushNotificationService === null) {
      return;
    }
    const effectRef = effect(() => {
      const isAuthenticated = this.#isAuthenticated();
      if (isAuthenticated) {
        if (this.#checkHasUserBeenShownSubscriptionPopup()) {
          return effectRef.destroy();
        }
        const status = this.#pushNotificationService!.status();
        if (status === 'SUPPORTED') {
          this.#ensureTimerStarted();
          effectRef.destroy();
        }
      }
    });
  }

  #checkHasUserBeenShownSubscriptionPopup(): boolean {
    assertIsNotNil(this.#window, 'Window not found');
    const result = this.#window.localStorage.getItem(LOCALSTORAGE_KEY);
    if (result === null) {
      return false;
    }
    try {
      return JSON.parse(result) ?? false;
    } catch {
      return false;
    }
  }

  #setUserHasSeenThePopup(): void {
    assertIsNotNil(this.#window, 'Window not found');
    this.#window.localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(true));
  }

  #ensureTimerStarted(): void {
    if (this.#timerId !== null) {
      return;
    }

    assertIsNotNil(window, 'Window not found');
    this.#timerId = window.setTimeout(
      () => {
        // Show overlay only if still not subscribed and supported
        assertIsNotNil(this.#pushNotificationService, 'Not found push notification service');
        if (this.#pushNotificationService.status() === 'SUPPORTED') {
          this.#openPushNotificationOverlay();
        }
      },
      5 * 60 * 1000,
    ); // 5 minutes
  }

  #openPushNotificationOverlay(): void {
    const { device } = UAParser(window.navigator.userAgent);

    const positionStrategy =
      device.is('mobile') || device.is('tablet')
        ? this.#overlay.position().global().centerHorizontally().bottom('16px')
        : this.#overlay.position().global().right('16px').bottom('16px');

    this.#overlayRef = this.#overlay.create({
      positionStrategy,
      hasBackdrop: false,
      panelClass: ['z-[1000]'],
      disposeOnNavigation: false,
      scrollStrategy: this.#overlay.scrollStrategies.reposition(),
    });

    const portal = new ComponentPortal(
      SubscribePushNotificationOverlayPanelComponent,
      null,
      Injector.create({
        parent: this.#injector,
        providers: [
          {
            provide: SUBSCRIBE_HANDLER_TOKEN,
            useValue: (loadingCallback => {
              this.#setUserHasSeenThePopup();
              loadingCallback?.(true);
              this.#pushNotificationService!.subscribe()?.then(result => {
                const close = (): void => {
                  loadingCallback?.(false);
                  this.#closeOverlay();
                };

                this.handleSubscribe(result, close);
              });
            }) as SubscribeHandler,
          },
          {
            provide: CLOSE_HANDLER_TOKEN,
            useValue: () => {
              this.#setUserHasSeenThePopup();
              this.#closeOverlay();
            },
          },
        ],
      }),
    );
    this.#overlayRef.attach(portal);
  }

  handleSubscribe(result: PushSubscription | 'HAS_NOT_PERMISSION' | null, close: () => void): void {
    if (result === 'HAS_NOT_PERMISSION') {
      openWarningToast({
        message: 'Értesítések fogadásához nincs jogosultsága az eszközön.',
      });
      close();
    } else if (result === null) {
      openWarningToast({
        message: 'Értesítések fogadásá nincs bekapcsolva az eszközön.',
      });
      close();
    } else {
      this.#pushNotificationService!.getFcmToken().then(deviceToken => {
        if (deviceToken === null) {
          openErrorToast({
            message: 'Ismeretlen hiba történt az értesítések feliratkozás közben.',
          });
          close();
        } else {
          this.#deviceTokenService
            .createOrUpdateDeviceToken({ deviceToken })
            .pipe(finalize(() => close()))
            .subscribe({
              next: () =>
                openSuccessToast({
                  message: 'Sikeresen feliratkozott az értesítésekre.',
                }),
              error: () =>
                openErrorToast({
                  message: 'Ismeretlen hiba történt az értesítések feliratkozás mentése közben.',
                }),
            });
        }
      });
    }
  }

  #closeOverlay(): void {
    if (this.#overlayRef !== null) {
      this.#overlayRef.dispose();
      this.#overlayRef = null;
    }
  }
}
